import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Button } from './ui/Button';
import { SlidersHorizontal, Calculator } from 'lucide-react';
interface SlideCalculatorProps {
  onContinue: () => void;
}
export function SlideCalculator({
  onContinue
}: SlideCalculatorProps) {
  const [revenueB, setRevenueB] = useState<number>(13.5);
  const [legacyPercent, setLegacyPercent] = useState<number>(0);
  const [improvementPercent, setImprovementPercent] = useState<number>(0.5);
  const [animationDone, setAnimationDone] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  useEffect(() => {
    const t0 = setTimeout(() => setRevenueB(13.5), 600);
    const t1 = setTimeout(() => setLegacyPercent(76), 1400);
    const t2 = setTimeout(() => setImprovementPercent(1), 2200);
    const t3 = setTimeout(() => setAnimationDone(true), 3200);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);
  const calculateUpside = () => revenueB * 1000 * (legacyPercent / 100) * (improvementPercent / 100);
  const [revealed, setRevealed] = useState(false);
  const upsideValue = useSpring(0, {
    bounce: 0,
    duration: 1600
  });
  const displayUpside = useTransform(upsideValue, latest => Math.round(latest).toLocaleString());
  useEffect(() => {
    if (revealed) upsideValue.set(calculateUpside());
  }, [revenueB, legacyPercent, improvementPercent, revealed]);
  const handleCalculate = () => {
    setRevealed(true);
  };
  const handleUserChange = (setter: (v: number) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInteracted(true);
    setter(Number(e.target.value));
  };
  return <div className="h-full w-full flex flex-col px-12 py-12 md:px-24 md:py-16 max-w-7xl mx-auto" data-id="element-174" data-mp="lj1023hi2ioo">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-white mb-8" data-id="element-175" data-mp="go57n28cptb1">
        Even a{' '}
        <span className="text-teal-400" data-id="element-176" data-mp="6zztozos6pwh">
          {improvementPercent.toFixed(1)}%
        </span>{' '}
        improvement across LEO's established portfolio =
      </motion.h2>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center" data-id="element-177" data-mp="3u45wtkwb87vd">
        <div className="lg:col-span-5 space-y-7 bg-navy-900/70 p-8 rounded-3xl border border-navy-600 relative" data-id="element-178" data-mp="lodtt2utc09c">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: animationDone && !userInteracted ? 1 : 0
        }} className="absolute -top-12 left-0 right-0 flex items-center justify-center gap-2" data-id="element-179" data-mp="pnmqafh8mc8l">
            <motion.div animate={{
            y: [0, -4, 0]
          }} transition={{
            repeat: Infinity,
            duration: 1.5
          }} className="flex items-center gap-2 bg-teal-400/20 text-teal-300 px-4 py-2 rounded-full text-sm font-medium border border-teal-400/30" data-id="element-180" data-mp="14lf2oyrhmkik">
              <SlidersHorizontal className="w-4 h-4" data-id="element-181" data-mp="1si1vl5ctpshl" />
              Adjust the sliders — set your own numbers
            </motion.div>
          </motion.div>

          <div className="space-y-3" data-id="element-182" data-mp="1nkii47ny9jqp">
            <div className="flex justify-between items-end" data-id="element-183" data-mp="zsbh2v7a4oky">
              <label className="text-white/80 font-medium" data-id="element-184" data-mp="zuxpsy5wpa4r">
                LEO Group Revenue
              </label>
              <span className="text-2xl font-serif text-white" data-id="element-185" data-mp="19r10wtiqkj3u">
                DKK {revenueB.toFixed(1)}bn
              </span>
            </div>
            <input type="range" min="1" max="30" step="0.5" value={revenueB} onChange={handleUserChange(setRevenueB)} className="w-full" data-id="element-186" data-mp="14r69mov1tri9" />
            <div className="flex justify-between text-xs text-white/40" data-id="element-187" data-mp="ppzd8eykgsyu">
              <span data-id="element-188" data-mp="prtxrs5xej2e">DKK 1bn</span>
              <span className="text-teal-400/60" data-id="element-189" data-mp="uou4m5alnpuk">2025: DKK 13.5bn</span>
              <span data-id="element-190" data-mp="ur6lsg8j9w6k">DKK 30bn</span>
            </div>
          </div>

          <div className="space-y-3" data-id="element-191" data-mp="znduggsbdls7">
            <div className="flex justify-between items-end" data-id="element-192" data-mp="zqjh7pdh0bkr">
              <label className="text-white/80 font-medium" data-id="element-193" data-mp="13qjvna0kkmrt">
                % from Established / Off-Patent Brands
              </label>
              <span className="text-2xl font-serif text-white" data-id="element-194" data-mp="18n0twx1hghim">
                DKK {(revenueB * legacyPercent / 100).toFixed(1)}bn
              </span>
            </div>
            <input type="range" min="10" max="90" step="1" value={legacyPercent} onChange={handleUserChange(setLegacyPercent)} className="w-full" data-id="element-195" data-mp="1if6j5z0coku4" />
            <div className="flex justify-between text-xs text-white/40" data-id="element-196" data-mp="1wdwjromdp2n8">
              <span data-id="element-197" data-mp="3gwgsj3p0axu3">10%</span>
              <span className="text-teal-400/60" data-id="element-198" data-mp="3gwqkgwtm3f2f">
                {legacyPercent}% — 2025: 76%
              </span>
              <span data-id="element-199" data-mp="3bwm2f2d22ca3">90%</span>
            </div>
          </div>

          <div className="space-y-3" data-id="element-200" data-mp="37vilecj9ua7a">
            <div className="flex justify-between items-end" data-id="element-201" data-mp="37wle8q05mmpu">
              <label className="text-white/80 font-medium" data-id="element-202" data-mp="32wyzud8lb0fg">
                Target Improvement
              </label>
              <span className="text-2xl font-serif text-teal-400" data-id="element-203" data-mp="3htzayn7pzdx">
                {improvementPercent.toFixed(1)}%
              </span>
            </div>
            <input type="range" min="0.5" max="5" step="0.5" value={improvementPercent} onChange={handleUserChange(setImprovementPercent)} className="w-full" data-id="element-204" data-mp="3vnqrqyimh2wz" />
            <div className="flex justify-between text-xs text-white/40" data-id="element-205" data-mp="n74ca4vkq68c">
              <span data-id="element-206" data-mp="iekx53h8n31f">0.5%</span>
              <span data-id="element-207" data-mp="id9sz2jvrt9s">5%</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col items-center lg:items-start justify-center text-center lg:text-left" data-id="element-208" data-mp="11c4pa1tmmqya">
          {revealed ? <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="space-y-4" data-id="element-209" data-mp="16c0qxueuq6ao">
              <h3 className="text-2xl text-white/60 font-medium uppercase tracking-widest" data-id="element-210" data-mp="nkq3xoc8ydo2">
                Unlocked Value
              </h3>
              <div className="flex items-baseline gap-3" data-id="element-211" data-mp="9m08se8zmo2c">
                <span className="text-4xl md:text-6xl font-serif text-teal-400 tracking-tight" data-id="element-212" data-mp="dmk216qfx7j7">
                  DKK
                </span>
                <motion.span className="text-6xl md:text-8xl lg:text-9xl font-serif text-teal-400 tracking-tight" data-id="element-213" data-mp="23vu0uwke0og0">
                  {displayUpside}
                </motion.span>
                <span className="text-4xl md:text-6xl font-serif text-teal-400" data-id="element-214" data-mp="3si3x0xryev6b">
                  m
                </span>
              </div>
              <p className="text-xl text-white/70 mt-6 border-l-2 border-teal-400/40 pl-4 py-2" data-id="element-215" data-mp="61pyvq3g1txc">
                Across 25+ brands and 70+ markets — value that is currently
                unmanaged and under-optimized.
              </p>
            </motion.div> : <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="space-y-6 max-w-md" data-id="element-216" data-mp="g2a1ox6m36ho">
              <h3 className="text-2xl text-white/60 font-medium uppercase tracking-widest" data-id="element-217" data-mp="2qrfei5bz1jr3">
                Unlocked Value
              </h3>
              <p className="text-xl text-white/70 leading-relaxed" data-id="element-218" data-mp="2luysbgoc1fgn">
                Set the sliders to match LEO's portfolio — then calculate the
                potential.
              </p>
              <motion.button type="button" onClick={handleCalculate} whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.98
          }} animate={{
            opacity: animationDone ? 1 : 0.4
          }} className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-teal-400 text-truewhite font-semibold text-lg shadow-lg shadow-teal-400/30 hover:bg-teal-500 transition-colors" data-id="element-219" data-mp="24x8d79pq6b20">
                <Calculator className="w-5 h-5" data-id="element-220" data-mp="1vzeig37lzpgy" />
                Calculate potential
              </motion.button>
            </motion.div>}

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: animationDone ? 1 : 0
        }} transition={{
          delay: 0.5
        }} className="mt-12" data-id="element-221" data-mp="32gt92gnejhuk"></motion.div>
        </div>
      </div>
    </div>;
}